import clsx from 'clsx';
import { useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react';

import styles from './OtpInput.module.scss';

type OtpInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  /** Called on any user interaction — handy for clearing a parent error. */
  onUserInput?: () => void;
  length?: number;
  autoFocus?: boolean;
  className?: string;
};

const OtpInput = ({
  value,
  onChange,
  onUserInput,
  length = 6,
  autoFocus,
  className,
}: OtpInputProps) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const focusInput = (index: number) => {
    const input = inputsRef.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...value];
    next[index] = char;
    onChange(next);
    onUserInput?.();

    if (char && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    onUserInput?.();

    if (e.key === 'Backspace') {
      if (value[index]) {
        e.preventDefault();
        const next = [...value];
        next[index] = '';
        onChange(next);
      } else if (index > 0) {
        // On an empty box, erase the previous digit right away rather than just selecting it,
        // so each Backspace removes one digit.
        e.preventDefault();
        const next = [...value];
        next[index - 1] = '';
        onChange(next);
        focusInput(index - 1);
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    }

    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (index: number) => (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;

    const next = [...value];
    pasted.split('').forEach((char, i) => {
      if (index + i < length) next[index + i] = char;
    });
    onChange(next);
    onUserInput?.();
    focusInput(Math.min(index + pasted.length, length - 1));
  };

  return (
    <div className={clsx(styles.boxes, className)}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          autoFocus={autoFocus && index === 0}
          className={styles.box}
          value={value[index] ?? ''}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onFocus={() => focusInput(index)}
          onPaste={handlePaste(index)}
        />
      ))}
    </div>
  );
};

export default OtpInput;
